import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { cn } from '../cn.js';

/**
 * Campo di form accessibile.
 *
 * Hint ed errore sono collegati all'input via `aria-describedby`: l'errore non e'
 * mai solo un bordo rosso (WCAG 1.4.1, 3.3.1). L'errore ha `role="alert"` cosi'
 * viene annunciato quando compare.
 */

interface FieldShellProps {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly children: (ids: { inputId: string; describedBy: string | undefined }) => ReactNode;
  readonly className?: string;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  children,
  className,
}: FieldShellProps): ReactNode {
  const base = useId();
  const inputId = `${base}-input`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;

  const describedByParts = [
    hint !== undefined ? hintId : null,
    error !== undefined ? errorId : null,
  ].filter((part): part is string => part !== null);
  const describedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={inputId} className="text-[14px] font-medium text-text">
        {label}
        {required ? (
          <span className="ml-1 text-text-muted" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {hint !== undefined ? (
        <p id={hintId} className="text-[13px] leading-5 text-text-muted">
          {hint}
        </p>
      ) : null}
      {children({ inputId, describedBy })}
      {error !== undefined ? (
        <p id={errorId} role="alert" className="text-[13px] leading-5 text-[var(--budget-blocked)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL_CLASSES =
  'min-h-[48px] w-full rounded-[var(--r-md)] border bg-surface px-3.5 text-[16px] text-text ' +
  'placeholder:text-text-subtle transition-colors duration-[var(--dur-fast)]';

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-describedby'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
}

export function TextField({ label, hint, error, className, ...rest }: TextFieldProps): ReactNode {
  return (
    <Field
      label={label}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      required={rest.required === true}
    >
      {({ inputId, describedBy }) => (
        <input
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={error !== undefined ? true : undefined}
          className={cn(
            CONTROL_CLASSES,
            error !== undefined ? 'border-[var(--budget-blocked)]' : 'border-border',
            className,
          )}
          {...rest}
        />
      )}
    </Field>
  );
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'aria-describedby'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly children: ReactNode;
}

export function SelectField({
  label,
  hint,
  error,
  className,
  children,
  ...rest
}: SelectFieldProps): ReactNode {
  return (
    <Field
      label={label}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      required={rest.required === true}
    >
      {({ inputId, describedBy }) => (
        <select
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={error !== undefined ? true : undefined}
          className={cn(
            CONTROL_CLASSES,
            error !== undefined ? 'border-[var(--budget-blocked)]' : 'border-border',
            className,
          )}
          {...rest}
        >
          {children}
        </select>
      )}
    </Field>
  );
}
